import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { TextView, Button, ChartTooltip } from '@atoms';
import { Breakdown, CommonCard, SkeletonChart } from '@organisms';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DateTime } from 'luxon';
import { useNegotiatorSrtTokens } from '@api';
import { toast } from 'react-toastify';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import CustomizedYAxisTick from '../particles/CustomizedYAxisTick';
import CustomizedXAxisTick from '../particles/CustomizedXAxisTick';

interface NegotiatorChartProps {
  billingAccount?: BillingAccount;
  dates: { start: DateTime | null; end: DateTime | null };
}

/**
 * Chart series, in stack order. The chart plots SRT only — call counts live
 * in the cards above and, per endpoint, in the collapsible table below, so
 * plotting them here as well was duplicating the same numbers in a third
 * place. `countKey` is retained because the cards still read it.
 */
const SERIES = [
  { countKey: 'applicantCount', srtKey: 'applicantSrt', label: 'Applicants', color: '#7c3aed' },
  { countKey: 'contactCount', srtKey: 'contactSrt', label: 'Contacts', color: '#086BDE' },
  { countKey: 'propertyCount', srtKey: 'propertySrt', label: 'Properties', color: '#0891b2' },
  {
    countKey: 'journalEntryCount',
    srtKey: 'journalEntrySrt',
    label: 'Activities & Syncs',
    color: '#16a34a'
  },
  { countKey: 'taskOfferCount', srtKey: 'taskOfferSrt', label: 'Tasks & Offers', color: '#F5CB5C' },
  { countKey: 'otherCount', srtKey: 'otherSrt', label: 'Other', color: '#94a3b8' }
] as const;

const EMPTY_DAY: Omit<NegotiatorUsageDay, 'date'> = {
  totalCalls: 0,
  srtToken: 0,
  applicantCount: 0,
  applicantSrt: 0,
  contactCount: 0,
  contactSrt: 0,
  propertyCount: 0,
  propertySrt: 0,
  journalEntryCount: 0,
  journalEntrySrt: 0,
  messageSyncCount: 0,
  messageSyncSrt: 0,
  taskOfferCount: 0,
  taskOfferSrt: 0,
  otherCount: 0,
  otherSrt: 0
};

const NegotiatorChart: React.FC<NegotiatorChartProps> = ({
  billingAccount,
  dates
}: NegotiatorChartProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [showEndpoints, setShowEndpoints] = useState<boolean>(false);

  const startDate = dates.start?.toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });

  const endDate = dates.end?.toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });

  const { data: response, isFetching: isLoading } = useNegotiatorSrtTokens({
    from: startDate,
    to: endDate,
    billingAccountId: billingAccount?.id
  });

  const breakdown = response?.data?.data;

  // The endpoint only returns days that had activity; fill the gaps so the
  // x-axis matches the selected range rather than collapsing empty days.
  const values = useMemo(() => {
    if (!breakdown?.days || !dates.start || !dates.end) return [];

    const dateMap = new Map<string, NegotiatorUsageDay>();
    breakdown.days.forEach((day) => {
      if (day.date) dateMap.set(day.date.substring(0, 10), day);
    });

    const filled: NegotiatorUsageDay[] = [];
    let day = dates.start;
    const lastDay = dates.end;
    while (day <= lastDay) {
      const key = day.toFormat('yyyy-MM-dd');
      filled.push(dateMap.get(key) ?? { date: day.toISO() ?? key, ...EMPTY_DAY });
      day = day.plus({ day: 1 });
    }
    return filled;
  }, [breakdown, dates.start, dates.end]);

  const totals = breakdown?.totals;
  const types = breakdown?.types ?? [];

  const nameMap = useMemo(
    () =>
      SERIES.reduce<Record<string, string>>((acc, series) => {
        acc[series.srtKey] = series.label;
        return acc;
      }, {}),
    []
  );

  const downloadLogs = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE;

    const queryString = `${endDate ? `&endDate=${endDate}` : ''}${
      startDate ? `&startDate=${startDate}` : ''
    }${billingAccount ? `&billingAccountId=${billingAccount?.id}` : ``}`;
    const urlApi = `${apiBase}/reapit/csv-download?${queryString}`;

    const zone: string[] = DateTime.now().toFormat('ZZ').split(':');
    const hour = Number(zone[0]);
    const mint = Number(zone[1]);
    const totalMint: number = hour * 60 + mint;

    const fetchOptions: RequestInit = {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        timezone: `${totalMint}`
      },
      method: 'GET'
    };

    const response: Response = await fetch(urlApi, fetchOptions);

    if (response.status == 200) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Negotiator ${DateTime.now().toFormat('LLL dd yyyy, hh:mm:ss')}.csv`
      );
      document.body.appendChild(link);
      link.click();
    } else {
      toast('Something went wrong');
    }
    setLoading(false);
  };

  return (
    <Box mt={2}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <TextView $fontSize={26} $fontWeight={700}>
          Negotiator
        </TextView>
        <Button label="Export" onClick={downloadLogs} size="small" disabled={loading} />
      </Stack>

      {/* One cell, matching the Lead Events / Broadcast Events pattern in
          totalTokenCusume: headline total on the card, categories as a
          Breakdown row underneath. The six category values sum to the
          headline, so no category may overlap another. */}
      <Box mt={2} mb={2}>
        <CommonCard
          title="Total API calls"
          count={totals?.totalCalls ?? 0}
          caption={`${(totals?.srtToken ?? 0).toFixed(2)} SRT billed`}
          icon={<ApiOutlinedIcon />}
          accent="secondary"
          loading={isLoading && !breakdown}
        >
          <Breakdown
            items={[
              { label: 'Applicants', value: totals?.applicantCount ?? 0 },
              { label: 'Contacts', value: totals?.contactCount ?? 0 },
              { label: 'Properties', value: totals?.propertyCount ?? 0 },
              { label: 'Activities & syncs', value: totals?.journalEntryCount ?? 0 },
              { label: 'Tasks & offers', value: totals?.taskOfferCount ?? 0 },
              { label: 'Other', value: totals?.otherCount ?? 0 }
            ]}
          />
        </CommonCard>
      </Box>

      {isLoading && !breakdown ? (
        <SkeletonChart height={340} />
      ) : (
        <Box
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.06)',
            border: '1px solid rgba(124, 58, 237, 0.15)',
            pt: 2,
            pb: 2,
            borderRadius: 2,
            height: 340
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={values} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis interval={0} dataKey="date" tick={(props) => CustomizedXAxisTick(props)} />
              <YAxis tick={(props) => CustomizedYAxisTick(props)} />
              <Tooltip
                content={
                  <ChartTooltip
                    nameMap={nameMap}
                    showTotal
                    filterZero
                    valueFormatter={(v) => Number(v).toFixed(2)}
                  />
                }
                cursor={{ fill: 'rgba(43, 34, 88, 0.06)' }}
              />
              {SERIES.map((series) => (
                <Bar
                  key={series.srtKey}
                  dataKey={series.srtKey}
                  name={series.label}
                  stackId="a"
                  fill={series.color}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {types.length > 0 && (
        <Box mt={1.5}>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowEndpoints((open) => !open)}
            endIcon={showEndpoints ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
            label={
              showEndpoints
                ? 'Hide Reapit calls by endpoint'
                : `See more — Reapit calls by endpoint (${types.length})`
            }
          />
          <Collapse in={showEndpoints} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                border: '1px solid rgba(15, 23, 42, 0.08)',
                borderRadius: 2,
                backgroundColor: '#ffffff',
                overflowX: 'auto'
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">SRT</TableCell>
                    <TableCell align="right">Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {types.map((row) => (
                    <TableRow key={`${row.type}-${row.action}`}>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.action}
                          color={row.action === 'write' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{row.calls.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.srtToken.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.share.toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default NegotiatorChart;
